import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Header from './header/Header';
import InspectionsImport from './pages/InspectionsImport';
import InspectorsImport from './pages/InspectorsImport';
import SelectionDashboard from './selection/SelectionDashboard'
import RoutesDashboard from './mapDashboard/RoutesDashboard'
import { rankInspectionRequests } from './calculations/sortInspections';
import Axios from 'axios';
import { AuthContext } from '../AuthContext';

function WorkflowMain() {

    const { currentUser, signOutUser } = useContext(AuthContext);
    const history = useHistory();
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    // Authentication check
    useEffect(() => {
        // Add a small delay to allow Firebase to initialize
        const timer = setTimeout(() => {
            setIsAuthLoading(false);
        }, 1000); // 1 second delay

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        // Only check authentication after the loading period
        if (!isAuthLoading) {
            if (!currentUser) {
                // No user logged in, redirect to DOB auth page
                history.push('/auth/nycdob');
                return;
            }

            // Check if the user's email is the authorized DOB email
            if (currentUser.email !== 'testnycdob@gmail.com' && currentUser.email !== 'tomvaz11@gmail.com' && currentUser.email !== 'testnycdob_read@gmail.com') {
                // Unauthorized user, redirect to DOB auth page
                history.push('/auth/nycdob');
                return;
            }

            // User is authenticated and authorized, continue with the workflow
            console.log('DOB user authenticated:', currentUser.email);
        }
    }, [currentUser, isAuthLoading, history]);

    // Initialize selectedDate based on current day
    const getInitialDate = () => {
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
        
        // If it's Thursday (4), set to next Monday
        if (dayOfWeek === 4) {
            const daysUntilMonday = 4; // 4 days from Thursday to Monday
            const nextMonday = new Date(today);
            nextMonday.setDate(today.getDate() + daysUntilMonday);
            return nextMonday;
        }
        
        // If it's Friday (5), set to next Tuesday
        if (dayOfWeek === 5) {
            const daysUntilTuesday = 4; // 4 days from Friday to Tuesday
            const nextTuesday = new Date(today);
            nextTuesday.setDate(today.getDate() + daysUntilTuesday);
            return nextTuesday;
        }
        
        // For other days, set to 2 days ahead
        const twoDaysAhead = new Date(today);
        twoDaysAhead.setDate(today.getDate() + 2);
        return twoDaysAhead;
    };

    const [selectedDate, setSelectedDate] = useState(getInitialDate());
    const [page, setPage] = useState('routes');

    // State for all imported data
    const [inspectionsData, setInspectionsData] = useState([]);
    const [inspectionDurationData, setInspectionDurationData] = useState([]);
    const [inspectorsData, setInspectorsData] = useState([]);
    const [calendarData, setCalendarData] = useState([]);
    const [holidayListData, setHolidayListData] = useState([]);
    const [districtData, setDistrictData] = useState([]);
    console.log({
        inspectionsData,
        inspectionDurationData, 
        inspectorsData,
        calendarData,
        holidayListData,
        districtData
    });

    const [optimizedRoutes, setOptimizedRoutes] = useState(
        [
            
        ]
    );

    // Track failed assignments from optimization
    const [failedAssignments, setFailedAssignments] = useState([]);
    
    // Track if we're coming from route creation to avoid unnecessary database retrieval
    const [isFromRouteCreation, setIsFromRouteCreation] = useState(false);
    
    // Track loading state for route retrieval
    const [isRetrievingRoutes, setIsRetrievingRoutes] = useState(false);

    // Handler for when inspection data is loaded
    const handleInspectionsLoaded = (data) => {
        setInspectionsData(data);
        console.log('Inspections loaded:', data);
    };

    // Handler for when inspection duration data is loaded
    const handleInspectionDurationLoaded = (data) => {
        setInspectionDurationData(data);
        console.log('Inspection duration data loaded:', data);
    };

    // Handler for when inspector data is loaded
    const handleInspectorsLoaded = (type, data) => {
        switch (type) {
            case 'inspectorList':
                setInspectorsData(data);
                console.log('Inspectors loaded:', data);
                break;
            case 'calendar':
                const standardizedCalendarData = data.map(entry => ({
                    ...entry,
                    START_TIME: standardizeTime(entry.START_TIME), 
                    END_TIME: standardizeTime(entry.END_TIME)
                }));
                setCalendarData(standardizedCalendarData);
                console.log('Calendar data loaded:', standardizedCalendarData);
                break;
            case 'holidayList':
                setHolidayListData(data);
                console.log('Holiday list loaded:', data);
                break;
            case 'district':
                setDistrictData(data);
                console.log('District data loaded:', data);
                break;
            default:
                console.warn('Unknown data type:', type);
        }
    };

    // Standardize the date format before setting the state
    const standardizeTime = (timeStr) => {
        // Handle the "DD-MMM-YY HH:MM AM/PM" format
        const formatTwo = /^(\d{1,2})-([A-Z]{3})-(\d{2})\s*(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
        if (formatTwo.test(timeStr)) {
            const monthMap = {
                'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
                'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
            };
            const [, d, m, y, h, min, ampm] = timeStr.match(formatTwo);
            let hours = parseInt(h);
            
            // Convert to 24-hour format
            if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
            
            // Format as "M/D/YY HH:mm"
            return `${monthMap[m.toUpperCase()]}/${d}/${y} ${hours.toString().padStart(2, '0')}:${min}`;
        }
        return timeStr; // Return as is if it's already in the correct format
    };

    // Add useEffect for retrieving routes when date changes on routes page
    useEffect(() => {
        // Only retrieve routes when we're on the routes page and selectedDate changes AND we're not coming from route creation
        if (page === 'routes' && !isFromRouteCreation) {
            retrieveRoutesForDate(selectedDate);
        }
        
        // Reset the flag after checking
        if (page === 'routes') {
            setIsFromRouteCreation(false);
        }
    }, [selectedDate, page]);

    // Function to retrieve routes from database using batched approach
    const retrieveRoutesForDate = async (date) => {
        try {
            setIsRetrievingRoutes(true); // Start loading
            const formattedDate = date.toISOString().split('T')[0];
            console.log('Retrieving routes for date:', formattedDate);
            
            let metadataResponse;
            try {
                // First, get metadata to check if routes exist and get total count
                metadataResponse = await Axios.post(`https://api.routora.com/get-inspector-routes-metadata`, {
                    selectedDate: formattedDate
                });
            } catch (error) {
                console.log('No routes found for date:', formattedDate);
                setOptimizedRoutes([]);
                return;
            }
            if (!metadataResponse.data.totalRoutes || metadataResponse.data.totalRoutes === 0) {
                console.log('No routes found for date:', formattedDate);
                setOptimizedRoutes([]);
                return;
            }
            
            const totalRoutes = metadataResponse.data.totalRoutes;
            console.log(`Found ${totalRoutes} routes for date:`, formattedDate);
            
            // Retrieve routes in parallel batches for better performance
            const allRoutes = new Array(totalRoutes); // Pre-allocate array to maintain order
            const batchSize = 10; // Process 10 routes at a time
            
            for (let batchStart = 0; batchStart < totalRoutes; batchStart += batchSize) {
                const batchEnd = Math.min(batchStart + batchSize, totalRoutes);
                const batchPromises = [];
                
                // Create promises for this batch
                for (let i = batchStart; i < batchEnd; i++) {
                    const promise = Axios.post(`https://api.routora.com/get-inspector-routes-chunked`, {
                        selectedDate: formattedDate,
                        index: i
                    }).then(response => ({
                        index: i,
                        route: response.data.route,
                        success: true
                    })).catch(error => ({
                        index: i,
                        error: error.message,
                        success: false
                    }));
                    
                    batchPromises.push(promise);
                }
                
                // Wait for this batch to complete
                const batchResults = await Promise.all(batchPromises);
                
                // Process results and maintain order
                batchResults.forEach(result => {
                    if (result.success && result.route) {
                        allRoutes[result.index] = result.route;
                        console.log(`Retrieved route ${result.index + 1} of ${totalRoutes}:`, result.route.assignedInspector.INSPECTOR_ID);
                    } else {
                        console.warn(`Failed to retrieve route at index ${result.index}:`, result.error);
                    }
                });
                
                console.log(`Completed batch ${Math.floor(batchStart/batchSize) + 1}: routes ${batchStart + 1}-${batchEnd}`);
            }
            
            setOptimizedRoutes(allRoutes);
            console.log('Successfully retrieved all routes:', allRoutes.length);
            
        } catch (error) {
            console.error('Error retrieving routes for date:', error);
            // Optionally show user-friendly error message
            setOptimizedRoutes([]);
        } finally {
            setIsRetrievingRoutes(false); // End loading regardless of success/failure
        }
    };

    // Show loading screen while authentication is initializing
    if (isAuthLoading) {
        return (
            <div className="flex flex-col min-h-screen overflow-hidden">
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <p className="text-sm text-gray-500 font-bold">
                            NYC DOB
                        </p>
                        <p className="text-sm text-gray-500">
                            Initializing authentication
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Don't render the main content if user is not authenticated or authorized
    if (!currentUser || (currentUser.email !== 'testnycdob@gmail.com' && currentUser.email !== 'tomvaz11@gmail.com' && currentUser.email !== 'testnycdob_read@gmail.com')) {
        return null; // This will show nothing while redirecting
    }

    return (
        <div className="flex flex-col min-h-screen overflow-hidden">
            <Header 
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
            />
            
            {/* Main content with sidebar */}
            <div className="flex flex-1">
                {/* Sidebar */}
                <div className="w-16 bg-myteal-500 flex flex-col items-center py-6 space-y-3 shadow-lg">
                    {/* Plus Button (Import) */}
                    <button
                        onClick={() => {
                            if (currentUser.email === 'testnycdob_read@gmail.com') {
                                alert('You\'re using a read-only account. While you can\'t start new routes, you can explore existing inspection itineraries by browsing the calendar above.');
                                return;
                            }
                            setPage('inspections');
                        }}
                        className={`w-12 h-12 mt-12 rounded-lg flex items-center justify-center transition-all duration-200 ${
                            page === 'inspections' || page === 'inspectors' || page === 'selection'
                                ? 'bg-white text-myteal-500 shadow-md' 
                                : 'text-white hover:bg-white/20'
                        }`}
                        title="Import Data"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                    </button>

                    {/* Map Button */}
                    <button
                        onClick={() => setPage('routes')}
                        className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-200 ${
                            page === 'routes' 
                                ? 'bg-white text-myteal-500 shadow-md' 
                                : 'text-white hover:bg-white/20'
                        }`}
                        title="Map View"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
                        </svg>
                    </button>

                    {/* Spacer to push logout to bottom */}
                    <div className="flex-1"></div>

                    {/* Logout Button */}
                    <button
                        onClick={signOutUser}
                        className="w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-200 text-white hover:bg-white/20"
                        title="Sign Out"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </button>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col">
                    {/* Page content */}
                    <main className="flex-1">
                        {page === 'inspections' && (
                            <InspectionsImport 
                                onInspectionsLoaded={handleInspectionsLoaded}
                                onInspectionDurationLoaded={handleInspectionDurationLoaded}
                                inspectionsData={inspectionsData}
                                inspectionDurationData={inspectionDurationData}
                                setPage={setPage}
                            />
                        )}
                        {page === 'inspectors' && (
                            <InspectorsImport 
                                onInspectorsLoaded={handleInspectorsLoaded}
                                inspectorsData={inspectorsData}
                                calendarData={calendarData}
                                holidayListData={holidayListData}
                                districtData={districtData}
                                setPage={setPage}
                            />
                        )}
                        {page === 'selection' && (
                            <div className="w-full h-full">
                                <SelectionDashboard
                                    inspections={{
                                        inspectionsData,
                                        inspectionDurationData
                                    }}
                                    inspectors={{
                                        inspectorsData,
                                        calendarData,
                                        holidayListData,
                                        districtData
                                    }}
                                    selectedDate={selectedDate}
                                    setOptimizedRoutes={setOptimizedRoutes}
                                    setFailedAssignments={setFailedAssignments}
                                    optimizedRoutes={optimizedRoutes}
                                    setPage={setPage}
                                    setIsFromRouteCreation={setIsFromRouteCreation}
                                />
                            </div>
                        )}
                        {page === 'routes' && (
                            <RoutesDashboard 
                                optimizedRoutes={optimizedRoutes}
                                failedAssignments={failedAssignments}
                                isRetrievingRoutes={isRetrievingRoutes}
                                selectedDate={selectedDate}
                                setPage={setPage}
                                currentUser={currentUser}
                            />
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}

export default WorkflowMain;